import classNames from "classnames";
import { BASIS_REQUIRED_MESSAGE } from "@components/accounting/migrationFileMatching";
import {
    MigrationRowStatus,
    MigrationStatusText,
} from "@components/accounting/migrationFileUploadShared";
import { HAS_ISSUES_MESSAGE } from "@components/accounting/migrationIssues";
import { EXTRACTION_WARNINGS_MESSAGE } from "@components/accounting/migrationExtractionWarnings";
import { BaseCellProps } from "@components/spreadsheet/cells";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { AlertCircleIcon, CheckIcon, SpinnerIcon } from "@icons";
import {
    ImportRejection,
    importRejectionSchema,
} from "@common/schemas/domains/accounting/import-rejection.schema";
import { ImportRejectionDetails } from "@components/accounting/ImportRejectionDetails/ImportRejectionDetails";
import styles from "./MigrationFileUploadTaskBody.module.scss";

// A short label (≤18 chars) is shown inline; the longer explanation (≤35
// chars) only lives in the hover tooltip so grid rows stay compact.
const StatusText = ({ text }: { text: MigrationStatusText }) => (
    <Tooltip
        content={text.tooltip}
        side="bottom"
        disabled={text.tooltip === text.short}
    >
        <span className={styles.statusText}>{text.short}</span>
    </Tooltip>
);

// `auto` text is self-evident and may wrap; `message` pairs a short label with
// a hover explanation. The split keeps one render path with correct tooltips.
type MigrationRowDisplay =
    | {
          variant: "auto";
          icon: React.ReactNode;
          className: string;
          text: string;
      }
    | {
          variant: "message";
          icon: React.ReactNode;
          className: string;
          text: MigrationStatusText;
      };

const alertIcon = (colorClass: string): React.ReactNode => (
    <AlertCircleIcon width={14} height={14} className={colorClass} />
);

const UNRECOGNIZED_LAST_RESORT: MigrationStatusText = {
    short: "Unrecognized",
    tooltip: "This file is not a valid migration document",
};

function getMigrationRowDisplay({
    status,
    summary,
    statusMessage,
}: {
    status: MigrationRowStatus;
    summary?: string;
    statusMessage?: MigrationStatusText;
}): MigrationRowDisplay {
    switch (status) {
        case "basis_required":
            return {
                variant: "message",
                icon: alertIcon("icon-color-yellow-600"),
                className: "text-yellow-600",
                text: statusMessage ?? BASIS_REQUIRED_MESSAGE,
            };
        case "extracting":
            return {
                variant: "auto",
                icon: <SpinnerIcon width={14} height={14} />,
                className: "text-gray-500",
                text: "Extracting…",
            };
        case "ready":
            return {
                variant: "auto",
                icon: <CheckIcon className="icon-color-gray-900" />,
                className: "text-gray-900",
                text: summary ?? "Ready",
            };
        case "awaiting":
            return {
                variant: "auto",
                icon: null,
                className: "text-gray-500",
                text: "Awaiting upload",
            };
        case "has_warnings":
            return {
                variant: "message",
                icon: alertIcon("icon-color-yellow-600"),
                className: "text-yellow-600",
                text: statusMessage ?? EXTRACTION_WARNINGS_MESSAGE,
            };
        case "has_issues":
            return {
                variant: "message",
                icon: alertIcon("icon-color-yellow-600"),
                className: "text-yellow-600",
                text: statusMessage ?? HAS_ISSUES_MESSAGE,
            };
        case "unrecognized":
            return {
                variant: "message",
                icon: alertIcon("icon-color-yellow-600"),
                className: "text-yellow-600",
                text: statusMessage ?? UNRECOGNIZED_LAST_RESORT,
            };
        default: {
            const exhaustive: never = status;
            return exhaustive;
        }
    }
}

export const MigrationStatusCellContent = ({ value, row }: BaseCellProps) => {
    const status = value as MigrationRowStatus;

    const display = getMigrationRowDisplay({
        status,
        summary: row.summary as string | undefined,
        statusMessage: row.statusMessage as MigrationStatusText | undefined,
    });
    // The loosely typed grid row demands runtime narrowing; the schema parse
    // replaces an explicit cast.
    const parsedRejection = importRejectionSchema.safeParse(row.rejection);
    const rejection: ImportRejection | undefined = parsedRejection.success
        ? parsedRejection.data
        : undefined;
    const documentId =
        typeof row.documentId === "number" ? row.documentId : undefined;
    const fileName =
        typeof row.fileName === "string" ? row.fileName : undefined;

    if (rejection && display.variant === "message") {
        return (
            <div
                className={classNames(styles.statusMessage, display.className)}
            >
                {display.icon}
                <ImportRejectionDetails
                    rejection={rejection}
                    trigger={display.text.short}
                    documentId={documentId}
                    fileName={fileName}
                />
            </div>
        );
    }

    return (
        <div className={classNames(styles.statusMessage, display.className)}>
            {display.icon}
            {display.variant === "auto" ? (
                <BreakableText
                    text={display.text}
                    showTooltip
                    className={styles.statusText}
                />
            ) : (
                <StatusText text={display.text} />
            )}
        </div>
    );
};
